/**
 * Canada Day Ultra Week
 *
 * Central campaign config for the temporary Ultra entitlement and in-app copy.
 *
 * Primary responsibilities:
 * - Define the promo window and copy
 * - Expose client-safe active-state helpers
 *
 * Dependencies:
 * - process.env
 */

export const CANADA_DAY_ULTRA_WEEK_ID = 'canada_day_ultra_week_2026'
export const CANADA_DAY_ULTRA_WEEK_CUSTOM_FEATURE_KEY = 'custom:canada_day_ultra_week'

export const CANADA_DAY_ULTRA_WEEK_DEFAULT_START = '2026-06-29T04:00:00.000Z'
export const CANADA_DAY_ULTRA_WEEK_DEFAULT_END = '2026-07-06T04:00:00.000Z'
export const CANADA_DAY_ULTRA_WEEK_HARD_STOP = '2026-07-08T04:00:00.000Z'
export const CANADA_DAY_ULTRA_WEEK_MAX_DURATION_DAYS = 7

export const CANADA_DAY_ULTRA_WEEK_COPY = {
  campaignName: 'Canada Day Ultra Week',
  headline: "Everyone's Going Ultra, Baby! 🇨🇦🇺🇸",
  body: "Canada made the Round of 16. Canada Day is here. The 4th of July is America's 250th anniversary. So there's only one word for a week like this: ULTRA.",
  offer: 'Free Losi Ultra for everyone this week.',
  shortBody: 'Free Losi Ultra for everyone this week. No coupon. No billing changes.',
  existingUltraBody:
    'You already had Ultra, but this week every Losi user gets to experience it too. Thanks for being ahead of the curve.',
  ctaLabel: 'Go Ultra',
  activatingLabel: 'Unlocking Ultra...',
  activatedHeadline: 'Ultra unlocked for the week',
  activatedBody:
    'You have Losi Ultra until the promo ends. Try bigger context, files, images, Spaces, planning, and search.',
  activatedCtaLabel: 'Start using Ultra',
  disclaimer: 'No coupon. No billing changes. Your plan goes back to normal after the promo.',
  badgeLabel: 'Ultra unlocked this week',
  existingUltraBadgeLabel: 'Canada Day Ultra Week is live'
} as const

export type CanadaDayUltraWeekState = {
  id: typeof CANADA_DAY_ULTRA_WEEK_ID
  isActive: boolean
  startsAt: string
  endsAt: string
  headline: string
  body: string
  offer: string
  ctaLabel: string
  disclaimer: string
  isExistingUltra: boolean
}

const SERVER_ENABLED_ENV_KEY = 'CANADA_DAY_ULTRA_WEEK_ENABLED'
const CLIENT_ENABLED_ENV_KEY = 'NEXT_PUBLIC_CANADA_DAY_ULTRA_WEEK_ENABLED'

const START_ENV_KEYS = [
  'CANADA_DAY_ULTRA_WEEK_START',
  'NEXT_PUBLIC_CANADA_DAY_ULTRA_WEEK_START'
] as const

const END_ENV_KEYS = ['CANADA_DAY_ULTRA_WEEK_END', 'NEXT_PUBLIC_CANADA_DAY_ULTRA_WEEK_END'] as const

function readEnv(keys: readonly string[]) {
  for (const key of keys) {
    const value = process.env[key]?.trim()
    if (value) return value
  }

  return null
}

function readBooleanEnv(keys: readonly string[]) {
  const value = readEnv(keys)?.toLowerCase()
  if (!value) return null

  if (['1', 'true', 'yes', 'on'].includes(value)) return true
  if (['0', 'false', 'no', 'off'].includes(value)) return false

  return null
}

function readRuntimeActivation() {
  const envKeys =
    typeof window === 'undefined' ? [SERVER_ENABLED_ENV_KEY] : [CLIENT_ENABLED_ENV_KEY]
  const configuredActivation = readBooleanEnv(envKeys)

  // The dated campaign is live by default inside its hard-stopped window.
  // Set the env value to false to disable it without shipping code.
  return configuredActivation ?? true
}

function readDateEnv(keys: readonly string[], fallback: string) {
  const value = readEnv(keys) ?? fallback
  const date = new Date(value)

  return Number.isNaN(date.getTime()) ? fallback : date.toISOString()
}

function isUltraLikePlan(plan: string | null | undefined) {
  const normalizedPlan = plan?.toLowerCase() || ''

  return (
    normalizedPlan.startsWith('ultra') ||
    normalizedPlan.startsWith('premium') ||
    normalizedPlan.startsWith('studio')
  )
}

function isValidCampaignWindow(startsAt: string, endsAt: string) {
  const startsAtTime = new Date(startsAt).getTime()
  const endsAtTime = new Date(endsAt).getTime()
  const hardStopTime = new Date(CANADA_DAY_ULTRA_WEEK_HARD_STOP).getTime()
  const maxDurationMs = CANADA_DAY_ULTRA_WEEK_MAX_DURATION_DAYS * 24 * 60 * 60 * 1000

  if (
    !Number.isFinite(startsAtTime) ||
    !Number.isFinite(endsAtTime) ||
    !Number.isFinite(hardStopTime)
  ) {
    return false
  }

  const durationMs = endsAtTime - startsAtTime

  return durationMs > 0 && durationMs <= maxDurationMs && endsAtTime <= hardStopTime
}

export function getCanadaDayUltraWeekWindow() {
  return {
    startsAt: readDateEnv(START_ENV_KEYS, CANADA_DAY_ULTRA_WEEK_DEFAULT_START),
    endsAt: readDateEnv(END_ENV_KEYS, CANADA_DAY_ULTRA_WEEK_DEFAULT_END)
  }
}

export function isCanadaDayUltraWeekActive(now: Date = new Date()) {
  if (!readRuntimeActivation()) return false

  const { startsAt, endsAt } = getCanadaDayUltraWeekWindow()
  const startsAtTime = new Date(startsAt).getTime()
  const endsAtTime = new Date(endsAt).getTime()
  const hardStopTime = new Date(CANADA_DAY_ULTRA_WEEK_HARD_STOP).getTime()
  const nowTime = now.getTime()

  if (!Number.isFinite(startsAtTime) || !Number.isFinite(endsAtTime)) {
    return false
  }

  if (!isValidCampaignWindow(startsAt, endsAt)) return false
  if (Number.isFinite(hardStopTime) && nowTime >= hardStopTime) return false

  return nowTime >= startsAtTime && nowTime < endsAtTime
}

export function getCanadaDayUltraWeekState(input?: {
  plan?: string | null
  now?: Date
}): CanadaDayUltraWeekState {
  const { startsAt, endsAt } = getCanadaDayUltraWeekWindow()

  return {
    id: CANADA_DAY_ULTRA_WEEK_ID,
    isActive: isCanadaDayUltraWeekActive(input?.now),
    startsAt,
    endsAt,
    headline: CANADA_DAY_ULTRA_WEEK_COPY.headline,
    body: isUltraLikePlan(input?.plan)
      ? CANADA_DAY_ULTRA_WEEK_COPY.existingUltraBody
      : CANADA_DAY_ULTRA_WEEK_COPY.body,
    offer: CANADA_DAY_ULTRA_WEEK_COPY.offer,
    ctaLabel: CANADA_DAY_ULTRA_WEEK_COPY.ctaLabel,
    disclaimer: CANADA_DAY_ULTRA_WEEK_COPY.disclaimer,
    isExistingUltra: isUltraLikePlan(input?.plan)
  }
}
